/**
 * Workbench Prescriptive — AI orchestration next-action preview (wiring V0).
 * Projection-only · no ControlledActionPort · ASSISTIVE_AI_LIVE=false.
 * Proof markers (grantsAuthority) only in URL-gated proof mode.
 */
import React, { useMemo } from 'react';
import { Alert, Space, Tag, Typography } from 'antd';
import { projectWorkbenchNextActionPreviewV0 } from '@shared/ai-orchestration-surface-contracts/workbenchNextActionProjectionV0';
import { OperatorProofModeGateV0 } from '@shared/operational-surface-responsive-composition';
import { useOperatorProofModeV0 } from '../../../hooks/ui/useOperatorProofMode';
import styles from './OrchestrationNextActionPreview.module.css';

const { Text, Paragraph } = Typography;

export interface OrchestrationNextActionPreviewProps {
  tenantId: string;
  taskId: string;
  requestingActorId?: string;
  evidenceRefs?: readonly string[];
  recommendationSummary?: string;
}

export function OrchestrationNextActionPreview({
  tenantId,
  taskId,
  requestingActorId = 'workbench_operator',
  evidenceRefs,
  recommendationSummary,
}: OrchestrationNextActionPreviewProps): React.ReactElement {
  const { surfaceClass } = useOperatorProofModeV0();
  const vm = useMemo(
    () =>
      projectWorkbenchNextActionPreviewV0({
        tenantId,
        requestingActorId,
        taskId,
        evidenceRefs,
        recommendationSummary,
      }),
    [tenantId, requestingActorId, taskId, evidenceRefs, recommendationSummary]
  );

  return (
    <section
      className={styles.root}
      data-testid="ai-orchestration-next-action-preview"
      data-operator-surface-class={surfaceClass}
      aria-label="AI next-action preview"
    >
      <div className={styles.header}>
        <Text strong>Next-action proposal</Text>
        <Space size={4} wrap>
          <Tag data-testid="ai-orch-slot">{vm.surfaceSlot}</Tag>
          <Tag color="gold" data-testid="ai-orch-admission">
            {vm.uxAdmission}
          </Tag>
          <OperatorProofModeGateV0 surfaceClass={surfaceClass}>
            <Tag data-testid="ai-orch-grants-authority">grantsAuthority=false</Tag>
          </OperatorProofModeGateV0>
        </Space>
      </div>

      <Paragraph className={styles.summary} data-testid="ai-orch-recommendation">
        {vm.recommendationSummary}
      </Paragraph>

      <dl className={styles.meta}>
        <div>
          <dt>Suggested action</dt>
          <dd data-testid="ai-orch-suggested-action">
            <Text code>{vm.suggestedActionKey}</Text>
            {vm.suggestedActionInCatalog ? null : <Text type="danger"> (not in V0 catalog)</Text>}
          </dd>
        </div>
        <div>
          <dt>Proposal status</dt>
          <dd data-testid="ai-orch-proposal-status">{vm.proposalStatus}</dd>
        </div>
        <div>
          <dt>Provenance</dt>
          <dd data-testid="ai-orch-provenance">{vm.provenance}</dd>
        </div>
        <div>
          <dt>Sources</dt>
          <dd data-testid="ai-orch-evidence">{vm.evidenceRefs.join(', ')}</dd>
        </div>
        <div>
          <dt>Execution</dt>
          <dd data-testid="ai-orch-execution-denied">denied — {vm.executionDenyReason}</dd>
        </div>
        <div>
          <dt>Mutation Δ</dt>
          <dd data-testid="ai-orch-mutation-delta">
            business={vm.businessMutationDelta} · CAP invokes={vm.controlledActionPortInvokes}
          </dd>
        </div>
      </dl>

      <Alert
        type="info"
        showIcon
        className={styles.alert}
        message="Preview only — not a receipt"
        description={vm.disclaimer}
        data-testid="ai-orch-disclaimer"
      />
    </section>
  );
}

export default OrchestrationNextActionPreview;
